import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Wifi, AlertTriangle } from "@/components/icons";
import { claimUnit } from "@/lib/api";
import { HttpError } from "@/lib/http";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

/**
 * Residential driver self-claim dialog. A logged-in driver enters the ID printed
 * on their charger and claims it; the server verifies possession by the charger
 * having reported from the same public IP as this request. Errors are surfaced
 * by the server's machine-readable `error` code (never the message string) —
 * see docs/api/unit-claim.md.
 */

// Errors hold a catalog key chosen by the server's error code, translated at
// render; any other failure gets the shared status/network message or the generic one (LOC-707).
type ClaimError =
  | { kind: "field"; messageKey: string }
  | { kind: "callout"; variant: "already_claimed"; maskedEmail: string | null }
  | { kind: "callout"; variant: "message"; messageKey: string }
  | { kind: "callout"; variant: "server"; error: unknown };

const CALLOUT_KEY: Record<string, string> = {
  ip_mismatch: "operator.claim.error_ip_mismatch",
  unit_unreachable: "operator.claim.error_unit_unreachable",
  not_residential: "operator.claim.error_not_residential",
  unit_limit_reached: "operator.claim.error_unit_limit_reached",
  not_residential_user: "operator.claim.error_not_residential_user",
};

function mapClaimError(err: unknown): ClaimError {
  const body =
    err instanceof HttpError && err.body && typeof err.body === "object"
      ? (err.body as Record<string, unknown>)
      : {};
  const code = typeof body.error === "string" ? body.error : "";

  if (code === "already_claimed") {
    return {
      kind: "callout",
      variant: "already_claimed",
      maskedEmail: typeof body.owner_email_masked === "string" ? body.owner_email_masked : null,
    };
  }
  if (code === "unit_not_found") return { kind: "field", messageKey: "operator.claim.error_unit_not_found" };
  if (CALLOUT_KEY[code]) return { kind: "callout", variant: "message", messageKey: CALLOUT_KEY[code] };
  return { kind: "callout", variant: "server", error: err };
}

export function ClaimChargerModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const t = useT();
  const [unitId, setUnitId] = useState("");
  const [err, setErr] = useState<ClaimError | null>(null);
  const qc = useQueryClient();

  // Reset transient state whenever the dialog is opened or closed.
  useEffect(() => {
    if (!open) {
      setUnitId("");
      setErr(null);
    }
  }, [open]);

  const claimM = useMutation({
    mutationFn: (id: string) => claimUnit(id),
    onSuccess: (res) => {
      toast.success(res.already_owner ? t("operator.claim.already_owner") : t("operator.claim.claimed"));
      // Bring the newly-owned charger into the driver's list.
      qc.invalidateQueries({ queryKey: ["units"] });
      onOpenChange(false);
    },
    onError: (e) => setErr(mapClaimError(e)),
  });

  const trimmed = unitId.trim();
  const submit = () => {
    if (!trimmed || claimM.isPending) return;
    setErr(null);
    claimM.mutate(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("operator.claim.open")}</DialogTitle>
          <DialogDescription>{t("operator.claim.description")}</DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div>
            <Label htmlFor="claim-unit-id">{t("operator.claim.id_label")}</Label>
            <Input
              id="claim-unit-id"
              className="mt-2 font-mono"
              placeholder={t("operator.claim.id_placeholder", { example: "0910041270000500000000620001" })}
              value={unitId}
              onChange={(e) => {
                setUnitId(e.target.value);
                if (err) setErr(null);
              }}
              autoFocus
              aria-invalid={err?.kind === "field" || undefined}
              aria-describedby={err?.kind === "field" ? "claim-unit-id-error" : undefined}
            />
            {err?.kind === "field" && (
              <p id="claim-unit-id-error" className="mt-1.5 text-sm text-destructive">{t(err.messageKey)}</p>
            )}
          </div>

          <div className="flex gap-2 rounded-xl bg-primary/10 p-3 text-sm text-primary">
            <Wifi className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t("operator.claim.wifi_hint")}</span>
          </div>

          {err?.kind === "callout" && (
            <div role="alert" className="flex gap-2.5 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              {err.variant === "already_claimed" ? (
                <span>
                  {err.maskedEmail
                    ? t("operator.claim.error_already_claimed", { email: err.maskedEmail })
                    : t("operator.claim.error_already_claimed_unknown")}
                </span>
              ) : err.variant === "message" ? (
                <span>{t(err.messageKey)}</span>
              ) : (
                <span>{errorMessage(t, err.error, { fallback: "operator.claim.error_generic" })}</span>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.action.cancel")}
            </Button>
            <Button type="submit" disabled={!trimmed || claimM.isPending}>
              {claimM.isPending ? t("operator.claim.submitting") : t("operator.claim.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
